<script setup>
import axios from 'axios';
import VSelect from 'vue-select';
import { ref, computed, onMounted, watch, useId } from 'vue';
import { successAlert, errorAlert } from "@/utils";
import AdminBlock from '@/components/movements/components/AdminBlock.vue';
import DriverSelect from '@/components/movements/components/DriverSelect.vue';
import InputTextField from '@/components/movements/components/InputTextField.vue';
import InputNumberField from "@/components/movements/components/InputNumberField.vue";
import MovementSelect from "@/components/movements/components/MovementSelect.vue";
import FileUpload from '@/components/movements/components/FileUpload.vue';
import Modal from 'bootstrap/js/dist/modal';

const props = defineProps({
    identifier: {
        type: String,
        default: '',
        required: false,
    },
    movements: {
        type: Array,
        default: () => [],
        required: false,
    },
    job: {
        type: Object,
        default: () => {},
        required: false,
    },
    expense: {
        type: Object,
        default: () => {},
        required: false,
    },
    isEdit: {
        type: Boolean,
        default: false,
        required: false,
    },
    modalFor: {
        type: String,
        default: '',
        required: false,
    }
});

const emit = defineEmits(['expense-created']);

const label = ref('');
const fuel = ref(null);

const evidenceReceived = ref(false);
const rechargeClient = ref(false);

const selectedMovement = ref(null);
const selectedDriver = ref(null);
const selectedPaymentMethod = ref('');

const total = ref(null);

const modal = ref({});
const files = ref([]);

const fileUpload = ref(null);
const fileInternalSignatures = ref([]);

const evidenceReceivedId = useId() + '_evidence_received';
const rechargeClientId = useId() + '_recharge_client';

const updateMovement = function (value) {
    selectedMovement.value = value;
    if (value && value.driver) {
        selectedDriver.value = value.driver;
    }
};

const getModalType = () => {
    return props.modalFor !== '' ? props.modalFor + '_' : '';
}

const getFileUploadRoute = () => {
    return route('api.movements.expenses.get-upload-url');
}

const handleFileUploaded = (signatures) => {
    fileInternalSignatures.value = signatures;
}

const resetFields = () => {
    label.value = '';
    fuel.value = null;
    evidenceReceived.value = false;
    rechargeClient.value = false;
    selectedMovement.value = null;
    selectedDriver.value = null;
    selectedPaymentMethod.value = '';
    total.value = null;
    files.value = [];
}

const createNewExpense = () => {

    if (!fileInternalSignatures.value || fileInternalSignatures.value.length === 0) {
        if (!confirm('Do you want to continue without adding photo evidence?')) {
            e.preventDefault();
            return;
        }
    }

    const data = {
        movement_id: selectedMovement.value?.id ?? null,
        driver_id: selectedDriver.value?.id ?? null,

        label: label.value,
        payment_method: selectedPaymentMethod.value,
        evidence_received: evidenceReceived.value,
        recharge_client: rechargeClient.value,

        fuel: fuel.value || null,
        total: total.value || null,

        receipt_file_signature: fileInternalSignatures.value,
    };

    const url = props.isEdit
        ? route('api.movements.expenses.update', { expense: props.expense.id })
        : route('api.movements.expenses.store');

    const method = props.isEdit ? 'put' : 'post';

    axios[method](url, data, {
        headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
        }
    })
        .then(response => {
            emit('expense-created', response.data.expense);
            // resetFields();
            const text = props.isEdit ? 'Expense Updated!' : 'Expense Created!';
            successAlert("Success", text);
            window.jQuery('.modal-header .btn-close').click();
        })
        .catch(errors => {
            errorAlert(errors, 'Error Creating Fuel Expense');
        });
}

const updateDriver = (value) => {
    selectedDriver.value = value;
}

const compModalIdentifier = computed(() => {
    return props.identifier + '_fuel_modal';
});

onMounted(() => {
    modal.value = new Modal(document.getElementById(compModalIdentifier.value));
});

watch(() => props.expense, (newExpense) => {
    label.value = newExpense?.label || '';
    fuel.value = newExpense?.fuel || null;
    evidenceReceived.value = !!newExpense?.evidence_received || false;
    rechargeClient.value = !!newExpense?.recharge_client || false;
    selectedDriver.value = newExpense?.driver || null;
    selectedPaymentMethod.value = newExpense?.payment_method || '';
    total.value = newExpense?.total || null;
    selectedMovement.value = newExpense?.movement || (props.movements.find(m => m.id === newExpense?.movement_id) || null);
    files.value = newExpense?.files || [];
}, { immediate: true });
</script>

<template>
    <div class="modal" :id="compModalIdentifier" tabindex="-1">
        <div class="modal-dialog modal-dialog-centered" style="max-width: 900px;">
            <div class="modal-content">
                <div class="modal-header">
                    <h5 class="modal-title text-center">
                        {{ isEdit ? 'Edit' : 'Create' }} Fuel Expense
                    </h5>
                    <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                </div>
                <div class="modal-body bg-light">
                    <fieldset>
                        <div class="row">
                            <div class="col-12">
                                <AdminBlock>
                                    <MovementSelect
                                        :movements="movements"
                                        :job="job"
                                        remove-margin
                                        required
                                        @update-movement="updateMovement"
                                    />
                                </AdminBlock>
                            </div>
                        </div>

                        <div class="row">
                            <div class="col-lg-6">
                                <AdminBlock title="Expense">
                                    <InputTextField
                                        name="Label"
                                        label="label"
                                        placeholder="Label"
                                        required
                                        v-model="label"
                                    />

                                    <InputNumberField
                                        name="fuel"
                                        label="Fuel (Optional)"
                                        placeholder="Fuel"
                                        append="Litres"
                                        v-model="fuel"
                                    />

                                    <div class="">
                                        <div class="mb-2">
                                            <input
                                                v-model="evidenceReceived"
                                                class="me-2"
                                                type="checkbox"
                                                name="evidence_received"
                                                :id="evidenceReceivedId"
                                            />
                                            <label :for="evidenceReceivedId">
                                                <strong>
                                                    Evidence Received (If assigned to Driver)
                                                </strong>
                                            </label>
                                        </div>

                                        <div>
                                            <input
                                                v-model="rechargeClient"
                                                class="me-2"
                                                type="checkbox"
                                                name="recharge_client"
                                                :id="rechargeClientId"
                                            />
                                            <label :for="rechargeClientId">
                                                <strong>
                                                    Recharge Client
                                                </strong>
                                            </label>
                                        </div>
                                    </div>
                                </AdminBlock>

                                <AdminBlock
                                    title="Driver"
                                    class="mb-0"
                                >
                                    <p>
                                        If the Expense was incurred by a Driver and needs paying back to them, please select them below.
                                    </p>

                                    <DriverSelect
                                        :driver="selectedDriver"
                                        required
                                        removeMargin
                                        @update-driver="updateDriver"
                                    />
                                </AdminBlock>
                            </div>

                            <div class="col-lg-6">
                                <AdminBlock
                                    title="Cost"
                                >
                                    <div class="mb-4">
                                        <label for="">
                                            <strong>
                                                Payment Method
                                            </strong>
                                            <span class="text-danger">*</span>
                                        </label>

                                        <v-select
                                            :options="[
                                                'Cash',
                                                'Card',
                                                'By Admin'
                                            ]"
                                            placeholder="Payment Method"
                                            v-model="selectedPaymentMethod"
                                            required
                                        />
                                    </div>

                                    <InputTextField
                                        name="total"
                                        label="Total (Optional if fuel)"
                                        placeholder="0.00"
                                        prepend="£"
                                        v-model="total"
                                        removeMargin
                                    />
                                </AdminBlock>

                                <AdminBlock
                                    title="Images"
                                    class="mb-0"
                                >
                                    <p>Please upload your receipts where possible</p>

                                    <FileUpload
                                        :file-upload-route="getFileUploadRoute()"
                                        name="receipt_file_signature"
                                        ref="fileUpload"
                                        no-margin
                                        multiple
                                        @file-uploaded="handleFileUploaded"
                                    />

                                </AdminBlock>
                            </div>
                        </div>
                    </fieldset>
                </div>

                <div class="modal-footer">
                    <button type="button"
                            :id="'create_' + getModalType() + 'contact_close_button'"
                            class="btn btn-secondary"
                            data-bs-dismiss="modal">Close</button>

                    <button type="button" class="btn btn-primary" @click="createNewExpense">
                        {{ isEdit ? 'Update' : 'Save' }}
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>
